/** @format */

/* eslint react-hooks/rules-of-hooks: "warn" */
import { useTranslation } from 'next-i18next';
import React, { useContext } from 'react';

import {
    QueryResponse,
    SearchListingProduct,
} from '@toucan/common/services/search/types';

import {
    centToDecimal,
    formatAsCurrency,
} from '@toucan/client/global/currency';
import { MarketContext } from '@toucan/client/global/market';
import { WrappedLink } from '@toucan/client/global/nextjs';
import {
    Product,
    formatPriceModifiers,
    getProductAttributes,
    getProductIncludesList,
    productConditionStringToEnum,
} from '@toucan/client/global/product';

import { getProductListingQuery } from '@toucan/client/core/search';

import { LinkVariant } from '@toucan/client/shared/components/link';

import { SearchResultsType } from '@toucan/client/features/search/components/listing-page';
import {
    ProductCard,
    ProductCardButtons,
} from '@toucan/client/features/search/components/product-card';

export function productListingHelper(
    formatPrice: ReturnType<typeof formatAsCurrency>,
    showProductNames: boolean
) {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);

    /**
     * Converts data from the API into an object to be used by the product card
     *
     * @param product The model data to map
     */
    const convertItemDataToProduct = (
        product: SearchListingProduct
    ): Product => {
        const id = parseInt(product.productId?.values[0] || '0');
        const name = product.modelName?.values[0] || '';
        const sku = product.productSku?.values[0] || '';
        const slug = product.modelUrlSegment?.values[0];
        const productSlug = t('product', { ns: 'urls' });
        const linkHref = `/${activeMarket.slug}/${productSlug}/${slug}/sku-${sku}`;
        const linkTemplatePath = `/product?slug=${slug}&sku=${sku}`;
        const image = product.productImages?.values[0] || '';

        const hasDiscount =
            parseInt(product.productPriceReduction?.values[0] || '0') > 0;

        const productPrice = hasDiscount
            ? product.productPriceOriginal?.values[0]
            : product.productPrice?.values[0];

        const discountPrice = hasDiscount
            ? formatPrice(
                  centToDecimal(
                      parseInt(product.productPrice?.values[0] || '0', 10)
                  )
              )
            : undefined;

        const value = parseInt(productPrice || '0', 10);
        const price = formatPrice(centToDecimal(value), {
            trailingZeros: false,
        });

        const condition = productConditionStringToEnum(
            product.productCondition?.values[0] || ''
        );
        // TODO - Handle if modelId isn't available, as there's probably bigger issues to sort out if thats the case
        const modelId = parseInt(product.modelId?.values?.[0] || '0');

        const shutterCount = product?.productShutterCount?.values[0]
            ? parseInt(product?.productShutterCount?.values[0], 10)
            : undefined;

        const hourCount = product?.productHourCount?.values[0]
            ? parseInt(product?.productHourCount?.values[0], 10)
            : undefined;

        const batteryChargeCount = product?.productBatteryChargeCount?.values[0]
            ? parseInt(product?.productBatteryChargeCount.values[0], 10)
            : undefined;

        const modelAvailableNew =
            product?.modelAvailableNew?.values[0] === 'True';

        const productPriceModifiers =
            product?.productPriceModifiers?.values ?? [];

        const description = getProductIncludesList(
            t,
            productPriceModifiers,
            modelAvailableNew,
            condition
        );

        const attributes = getProductAttributes({
            t,
            shutterCount,
            hourCount,
            batteryChargeCount,
            locale: activeMarket.iso,
            condition,
        });

        const priceModifiers = formatPriceModifiers(productPriceModifiers);

        return {
            id,
            name,
            sku,
            linkHref,
            linkTemplatePath,
            image,
            price,
            condition,
            modelId,
            discountPrice,
            description,
            value,
            attributes,
            priceModifiers,
            /**
             * TODO: Populate these fields
             * - priceAddendum
             */
        };
    };

    /**
     * Gets an array of product card/placeholder data to be applied to ModelCard components
     */
    function getProductCardData(
        searchResults: Omit<QueryResponse<SearchListingProduct>, 'facets'>,
        numResultSkeletons: number
    ) {
        return [
            ...searchResults.results.map((product) => {
                return convertItemDataToProduct(product);
            }),
            ...new Array(numResultSkeletons).fill(undefined),
        ];
    }

    /**
     * Gets either product cards or placeholders
     *
     * @param searchResults the search results
     * @param numResultSkeletons the number of loading search results to display skeletons for
     * @returns {ReactNode} The product cards to show
     */
    function getProductCards(
        searchResults: SearchResultsType<SearchListingProduct>,
        numResultSkeletons: number
    ) {
        return getProductCardData(searchResults, numResultSkeletons).map(
            (product, index) => {
                const href =
                    product?.linkTemplatePath || product?.linkHref || '';

                const key = `${index}-${product?.id}`;

                return (
                    <div key={key} className="link-wrapper">
                        <WrappedLink
                            className="card-link"
                            variant={LinkVariant.CUSTOM}
                            href={href}
                            as={product?.linkHref}
                            disabled={!href}
                            passHref
                            hasOutlineOnFocus
                        >
                            <ProductCard
                                product={product}
                                visibleButtons={[
                                    ProductCardButtons.BASKET,
                                    ProductCardButtons.SAVE,
                                ]}
                                showProductName={showProductNames}
                            />
                        </WrappedLink>
                    </div>
                );
            }
        );
    }

    return {
        getListingCards: getProductCards,
        getQuery: getProductListingQuery,
    };
}
